import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  trackPageView,
  trackScrollDepth,
  trackTimeOnPage,
  trackGalleryClick,
  trackFloorPlanOpen,
  trackDesignProjectClick,
  trackLightboxNavigation,
  trackLightboxClose,
  trackImagesViewed,
  trackPhoneClick,
  trackWhatsAppClick,
  trackSectionView,
} from '../analytics';

const PREVIEW_COUNT = 5;

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;
const DOUBLE_CLICK_ZOOM = 2.5;
const DRAG_THRESHOLD = 4; // px of movement before a drag suppresses the closing click
const SWIPE_THRESHOLD = 55; // px of horizontal travel that changes image
const SLIDE_MS = 320; // keep in step with the .lightbox-track transition

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function ApartmentPage({ property }) {
  const {
    id,
    path,
    documentTitle,
    metaDescription,
    phone,
    price,
    topBarNotes,
    badge,
    title,
    subtitle,
    gallery,
    heroImage,
    infoItems,
    floorPlan,
    designProject,
    features,
    location,
    footerText,
  } = property;

  const galleryImages = useMemo(() => gallery || [], [gallery]);
  const hasGallery = galleryImages.length > 0;

  // null, or { images, index, label } — any set of images can be opened
  const [lightbox, setLightbox] = useState(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [swipeDx, setSwipeDx] = useState(0);
  // Direction the track is animating toward: -1 previous, +1 next, 0 at rest
  const [slideDir, setSlideDir] = useState(0);
  const [instant, setInstant] = useState(false); // suppress the re-centring frame

  const pageStartTime = useRef(Date.now());
  const trackedScrollMilestones = useRef(new Set());
  const trackedSections = useRef(new Set());
  const lightboxRef = useRef(null);
  const imageRef = useRef(null);
  const dragStart = useRef(null);
  const pinchStart = useRef(null);
  const suppressClick = useRef(false);
  const viewedImages = useRef(new Set()); // distinct images seen this opening

  const designImages = useMemo(() => designProject?.images || [], [designProject]);
  const planImages = useMemo(() => (floorPlan ? [floorPlan.image] : []), [floorPlan]);

  const lightboxImages = lightbox?.images || [];
  const total = lightboxImages.length;
  const canSwipe = total > 1;
  // The track always holds previous / current / next so a swipe reveals a real
  // neighbour rather than swapping the image in place.
  const slideIndexes = canSwipe
    ? [(lightbox.index - 1 + total) % total, lightbox.index, (lightbox.index + 1) % total]
    : [];
  const lightboxSrc = lightbox ? lightboxImages[lightbox.index] : null;

  // Set the document title / meta description for this apartment
  useEffect(() => {
    document.title = documentTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && metaDescription) {
      meta.setAttribute('content', metaDescription);
    }
  }, [documentTitle, metaDescription]);

  // Track page view on mount (and whenever the route changes to another apartment)
  useEffect(() => {
    trackPageView(path);
  }, [path]);

  // Reset per-page tracking state when switching between apartments
  useEffect(() => {
    pageStartTime.current = Date.now();
    trackedScrollMilestones.current = new Set();
    trackedSections.current = new Set();
    setLightbox(null);
  }, [id]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      const milestones = [25, 50, 75, 90, 100];
      milestones.forEach((milestone) => {
        if (scrollPercent >= milestone && !trackedScrollMilestones.current.has(milestone)) {
          trackedScrollMilestones.current.add(milestone);
          trackScrollDepth(milestone);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track time on page when user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
      trackTimeOnPage(timeSpent);
    };

    // Also track periodically for long sessions (every 30 seconds)
    const intervalId = setInterval(() => {
      const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
      if (timeSpent > 0 && timeSpent % 30 === 0) {
        trackTimeOnPage(timeSpent);
      }
    }, 30000);

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(intervalId);
    };
  }, []);

  // Track section visibility using Intersection Observer
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionName = entry.target.dataset.section;
            if (sectionName && !trackedSections.current.has(sectionName)) {
              trackedSections.current.add(sectionName);
              trackSectionView(sectionName);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // Observe all sections with data-section attribute
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [id]);

  const resetZoom = useCallback(() => {
    setZoom(MIN_ZOOM);
    setOffset({ x: 0, y: 0 });
    setSwipeDx(0);
    setSlideDir(0);
  }, []);

  // Keep the image from being dragged out of view: at scale s the picture is
  // s times larger, so it may travel half the overflow in each direction.
  const clampOffset = useCallback((next, scale) => {
    const el = imageRef.current;
    if (!el) return next;
    const maxX = (el.offsetWidth * (scale - 1)) / 2;
    const maxY = (el.offsetHeight * (scale - 1)) / 2;
    return {
      x: clamp(next.x, -maxX, maxX),
      y: clamp(next.y, -maxY, maxY),
    };
  }, []);

  const applyZoom = useCallback(
    (nextZoom) => {
      const target = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
      setZoom(target);
      setOffset((prev) => (target === MIN_ZOOM ? { x: 0, y: 0 } : clampOffset(prev, target)));
    },
    [clampOffset]
  );

  const zoomBy = useCallback((delta) => applyZoom(zoom + delta), [applyZoom, zoom]);

  const openLightbox = (index) => {
    resetZoom();
    viewedImages.current = new Set([index]);
    setLightbox({ images: galleryImages, index, label: title, source: 'Gallery' });
    trackGalleryClick(index);
  };

  const openFloorPlan = () => {
    resetZoom();
    viewedImages.current = new Set([0]);
    setLightbox({ images: planImages, index: 0, label: 'Plan apartament', source: 'Plan' });
    trackFloorPlanOpen();
  };

  const openDesign = (index) => {
    resetZoom();
    viewedImages.current = new Set([index]);
    setLightbox({ images: designImages, index, label: designProject.heading, source: 'Design' });
    trackDesignProjectClick(index);
  };

  // A tel: link must not open in a new tab, and the two channels are worth
  // telling apart in analytics, so the channel drives both.
  const isWhatsApp = phone?.channel === 'whatsapp';
  const contactLinkProps = isWhatsApp
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  const handleContactClick = (place) => {
    if (isWhatsApp) trackWhatsAppClick(place);
    else trackPhoneClick(place);
  };

  const closeLightbox = useCallback(() => {
    if (lightbox) {
      trackLightboxClose(lightbox.index, lightbox.source);
      trackImagesViewed(lightbox.source, viewedImages.current.size, lightbox.images.length);
    }
    setLightbox(null);
    resetZoom();
  }, [lightbox, resetZoom]);

  // Start the slide. The index only changes once the animation has finished,
  // so the outgoing image stays on screen while it travels.
  const requestSlide = useCallback(
    (direction) => {
      if (!canSwipe || slideDir !== 0) return;
      setZoom(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
      setSlideDir(direction);
    },
    [canSwipe, slideDir]
  );

  // Commit on a timer rather than transitionend: deterministic, and it cannot
  // be missed if the transition is interrupted or never fires.
  useEffect(() => {
    if (slideDir === 0 || !canSwipe) return undefined;
    const id = setTimeout(() => {
      const newIndex = (lightbox.index + slideDir + total) % total;
      viewedImages.current.add(newIndex);
      trackLightboxNavigation(slideDir > 0 ? 'Next' : 'Previous', newIndex, lightbox.source);
      setInstant(true); // re-centre the track without animating back
      setLightbox((prev) => ({ ...prev, index: newIndex }));
      setSlideDir(0);
      setSwipeDx(0);
    }, SLIDE_MS);
    return () => clearTimeout(id);
  }, [slideDir, canSwipe, lightbox, total]);

  // Drop the no-transition flag once the re-centred frame has been painted
  useEffect(() => {
    if (!instant) return undefined;
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setInstant(false));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [instant]);

  const nextImage = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      requestSlide(1);
    },
    [requestSlide]
  );

  const prevImage = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      requestSlide(-1);
    },
    [requestSlide]
  );

  // Keyboard: navigate the gallery, zoom, and close
  useEffect(() => {
    if (!lightbox) return undefined;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'Escape':
          closeLightbox();
          break;
        case '+':
        case '=':
          zoomBy(ZOOM_STEP);
          break;
        case '-':
          zoomBy(-ZOOM_STEP);
          break;
        case '0':
          resetZoom();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox, nextImage, prevImage, closeLightbox, zoomBy, resetZoom]);

  // Wheel zoom and pinch zoom need non-passive listeners to suppress page scroll
  useEffect(() => {
    const node = lightboxRef.current;
    if (!node) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      applyZoom(zoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    };

    const distance = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinchStart.current = { distance: distance(e.touches), zoom };
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStart.current) {
        e.preventDefault();
        const ratio = distance(e.touches) / pinchStart.current.distance;
        applyZoom(pinchStart.current.zoom * ratio);
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStart.current = null;
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: false });
    node.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
    };
  }, [lightbox, zoom, applyZoom]);

  // Lock page scrolling while the lightbox is open
  useEffect(() => {
    if (!lightbox) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [lightbox]);

  const onPointerDown = (e) => {
    if (zoom <= MIN_ZOOM && !canSwipe) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
      moved: 0,
    };
    setIsDragging(true);
  };

  const onPointerMove = (e) => {
    const start = dragStart.current;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    start.moved = Math.max(start.moved, Math.abs(dx) + Math.abs(dy));

    if (zoom > MIN_ZOOM) {
      setOffset(clampOffset({ x: start.offsetX + dx, y: start.offsetY + dy }, zoom));
    } else if (canSwipe && Math.abs(dx) > Math.abs(dy)) {
      // Follow the finger so the swipe feels attached to the image
      setSwipeDx(dx);
    }
  };

  const onPointerUp = () => {
    const start = dragStart.current;
    dragStart.current = null;
    setIsDragging(false);
    if (!start) return;
    if (start.moved > DRAG_THRESHOLD) suppressClick.current = true;

    if (zoom <= MIN_ZOOM && canSwipe) {
      // Keep swipeDx: the track animates on from where the finger left it
      if (swipeDx <= -SWIPE_THRESHOLD) requestSlide(1);
      else if (swipeDx >= SWIPE_THRESHOLD) requestSlide(-1);
      else setSwipeDx(0); // under the threshold, spring back
    }
  };

  const handleBackdropClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    closeLightbox();
  };

  const toggleZoom = (e) => {
    e.stopPropagation();
    applyZoom(zoom > MIN_ZOOM ? MIN_ZOOM : DOUBLE_CLICK_ZOOM);
  };

  return (
    <div className="App">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-bar-content">
          {topBarNotes.map((note, index) => (
            <div
              className={`top-bar-item${note.highlight ? ' highlight' : ''}`}
              key={index}
            >
              <span>{note.icon}</span>
              <span>{note.text}</span>
            </div>
          ))}
          {price && (
            <div className="top-bar-item price">
              <span>💰</span>
              <span>{price}</span>
            </div>
          )}
          {phone && (
            <a
              href={phone.href}
              className="top-bar-phone"
              {...contactLinkProps}
              aria-label={phone.ariaLabel}
              onClick={() => handleContactClick('top-bar')}
            >
              <span>{phone.icon}</span>
              <span>{phone.display}</span>
            </a>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <header className="hero">
        <div className="hero-overlay"></div>
        {heroImage && <img src={heroImage} alt={title} className="hero-image" />}
        <div className="hero-content">
          <span className="badge">{badge}</span>
          <h1>{title}</h1>
          <p className="hero-subtitle">{subtitle}</p>
          {price && (
            <div className="price-tag">
              <span className="price">{price}</span>
            </div>
          )}
        </div>
        <div className="scroll-indicator">
          <span>Descoperă mai mult</span>
          <div className="scroll-arrow">↓</div>
        </div>
      </header>

      {/* Quick Info Bar */}
      <section className="info-bar">
        {infoItems.map((item, index) => (
          <div className="info-item" key={index}>
            <span className="info-icon">{item.icon}</span>
            <span className="info-text">{item.text}</span>
          </div>
        ))}
      </section>

      {/* Gallery Section */}
      {hasGallery && (
        <section className="gallery-section" data-section="gallery">
          <div className="section-header">
            <h2>Galerie Foto</h2>
            <div className="section-divider"></div>
            <p>Click pe o fotografie pentru a vedea toate cele {galleryImages.length} imagini</p>
          </div>
          <div className="gallery-grid gallery-preview">
            {galleryImages.slice(0, PREVIEW_COUNT).map((img, index) => (
              <div
                key={index}
                className={`gallery-item ${index === 0 ? 'featured' : ''}`}
                onClick={() => openLightbox(index)}
              >
                <img src={img} alt={`${title} ${index + 1}`} loading="lazy" />
                {index === PREVIEW_COUNT - 1 && galleryImages.length > PREVIEW_COUNT ? (
                  <div className="gallery-item-overlay gallery-more">
                    <span className="more-count">+{galleryImages.length - PREVIEW_COUNT}</span>
                    <span className="more-text">fotografii</span>
                  </div>
                ) : (
                  <div className="gallery-item-overlay">
                    <span className="zoom-icon">🔍</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Floor Plan Section */}
      {floorPlan && (
        <section className="floor-plan-section" data-section="floor-plan">
          <div className="section-header">
            <h2>{floorPlan.heading}</h2>
            <div className="section-divider"></div>
            <p>{floorPlan.description}</p>
          </div>
          <button type="button" className="floor-plan-container" onClick={openFloorPlan}>
            <img src={floorPlan.image} alt="Plan apartament" className="floor-plan-image" loading="lazy" />
            <span className="floor-plan-hint">🔍 Click pentru a mări</span>
          </button>
        </section>
      )}

      {/* Design Project Section — 3D renders, explicitly not photographs */}
      {designProject && designProject.images.length > 0 && (
        <section className="design-section" data-section="design-project">
          <div className="section-header">
            <h2>{designProject.heading}</h2>
            <div className="section-divider"></div>
            <p>{designProject.description}</p>
          </div>
          <p className="design-note">{designProject.note}</p>
          <div className="gallery-grid gallery-preview">
            {designProject.images.slice(0, PREVIEW_COUNT).map((img, index) => (
              <div
                key={index}
                className={`gallery-item ${index === 0 ? 'featured' : ''}`}
                onClick={() => openDesign(index)}
              >
                <img src={img} alt={`${designProject.heading} ${index + 1}`} loading="lazy" />
                {index === PREVIEW_COUNT - 1 && designProject.images.length > PREVIEW_COUNT ? (
                  <div className="gallery-item-overlay gallery-more">
                    <span className="more-count">+{designProject.images.length - PREVIEW_COUNT}</span>
                    <span className="more-text">randări</span>
                  </div>
                ) : (
                  <div className="gallery-item-overlay">
                    <span className="zoom-icon">🔍</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="features-section" data-section="features">
        <div className="section-header">
          <h2>Caracteristici</h2>
          <div className="section-divider"></div>
          <p>Detalii despre apartament și clădire</p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div className="feature-card" key={index}>
              <div className="feature-icon">{feature.icon}</div>
              <div className="feature-content">
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Location Section */}
      <section className="location-section" data-section="location">
        <div className="section-header">
          <h2>Locație</h2>
          <div className="section-divider"></div>
          <p>{location.summary}</p>
        </div>
        <div className="location-content">
          <div className="address-card">
            <div className="address-icon">📍</div>
            <div className="address-details">
              <h3>Adresă Completă</h3>
              {location.addressLines.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </div>
          <div className="map-container">
            <iframe
              src={location.mapEmbedSrc}
              width="100%"
              height="450"
              style={{border: 0}}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              title="Locație apartament"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      {phone && (
        <section className="contact-section" data-section="contact">
          <div className="contact-card">
            <div className="contact-header">
              <h2>Interesat?</h2>
              <p>{phone.intro}</p>
            </div>
            <div className="contact-info">
              <a
                href={phone.href}
                className="contact-phone"
                {...contactLinkProps}
                aria-label={phone.ariaLabel}
                onClick={() => handleContactClick('contact-section')}
              >
                <span className="phone-icon">{phone.icon}</span>
                <span className="phone-number">{phone.display}</span>
              </a>
              <p className="contact-cta">{phone.cta}</p>
            </div>
            {price && (
              <div className="price-reminder">
                <span className="label">Preț</span>
                <span className="value">{price}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="footer">
        <p>{footerText}</p>
      </footer>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" ref={lightboxRef} onClick={handleBackdropClick}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Închide">
            ×
          </button>

          {canSwipe && (
            <>
              <button className="lightbox-prev" onClick={prevImage} aria-label="Imaginea anterioară">
                ‹
              </button>
              <button className="lightbox-next" onClick={nextImage} aria-label="Imaginea următoare">
                ›
              </button>
            </>
          )}

          {canSwipe ? (
            <div
              className="lightbox-track"
              style={{
                transform:
                  slideDir === 0
                    ? `translateX(calc(-100% / 3 + ${swipeDx}px))`
                    : slideDir > 0
                    ? 'translateX(calc(-200% / 3))'
                    : 'translateX(0%)',
                transition:
                  instant || isDragging ? 'none' : `transform ${SLIDE_MS}ms ease-out`,
              }}
            >
              {slideIndexes.map((imageIndex, position) => {
                const isCurrent = position === 1;
                return (
                  // Keyed by position so the nodes persist and only src changes:
                  // the incoming image is already decoded from its neighbour slot.
                  <div className="lightbox-slide" key={position}>
                    <img
                      ref={isCurrent ? imageRef : undefined}
                      src={lightboxImages[imageIndex]}
                      alt={`${lightbox.label} ${imageIndex + 1}`}
                      className="lightbox-image"
                      draggable={false}
                      style={
                        isCurrent
                          ? {
                              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                              transition: isDragging ? 'none' : 'transform 0.2s ease',
                              cursor:
                                zoom > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                            }
                          : undefined
                      }
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={isCurrent ? toggleZoom : undefined}
                      onPointerDown={isCurrent ? onPointerDown : undefined}
                      onPointerMove={isCurrent ? onPointerMove : undefined}
                      onPointerUp={isCurrent ? onPointerUp : undefined}
                      onPointerCancel={isCurrent ? onPointerUp : undefined}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <img
              ref={imageRef}
              src={lightboxSrc}
              alt={lightbox.label}
              className="lightbox-image"
              draggable={false}
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
                cursor: zoom > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
              }}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={toggleZoom}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          )}

          <div className="lightbox-zoom" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => zoomBy(-ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} aria-label="Micșorează">
              −
            </button>
            <span className="lightbox-zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => zoomBy(ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} aria-label="Mărește">
              +
            </button>
            <button onClick={resetZoom} disabled={zoom === MIN_ZOOM} aria-label="Revino la dimensiunea inițială">
              ⟲
            </button>
          </div>

          {canSwipe && (
            <div className="lightbox-counter">
              {lightbox.index + 1} / {galleryImages.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ApartmentPage;
